import { useMemo, useState } from "react";
import { BarChart } from "@mantine/charts";
import { Badge, Chip, Group, Loader, Paper, Stack, Table, Title } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";
import { useProductSales } from "../../../shared/api";
import { EmptyState, MoneyDisplay, PageHeader } from "../../../shared/ui";

/** Date → "YYYY-MM-DD" (local TZ — toISOString UTC'ye kaydırıp yanlış güne
 * düşürmesin diye lokal alanlardan kurulur). */
function toLocalDateStr(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Bugün (00:00 local) — varsayılan tek-gün aralığı. */
function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Bu ayın ilk günü — "Bu Ay" hızlı seçimi. */
function startOfMonth(): Date {
  const d = today();
  d.setDate(1);
  return d;
}

export function ProductSalesPage() {
  // Varsayılan: BUGÜN (tek gün). İstenirse "Bu Ay" veya elle aralık.
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    today(),
    today(),
  ]);
  // Aktif hızlı seçim chip'i (görsel vurgu için). Elle aralık → null.
  const [activeChip, setActiveChip] = useState<"today" | "month" | null>("today");

  const params = useMemo(
    () => ({
      date_from: toLocalDateStr(dateRange[0]),
      date_to: toLocalDateStr(dateRange[1]),
    }),
    [dateRange],
  );
  const { data, isLoading } = useProductSales(params);
  const rows = data?.rows || [];

  // Aktif aralık tek gün mü (boş durum mesajını netleştirmek için).
  const hasRange = !!(dateRange[0] && dateRange[1]);
  const rangeLabel =
    activeChip === "today" ? "bugün"
    : activeChip === "month" ? "bu ay"
    : hasRange ? "seçili tarih aralığında"
    : "seçili dönemde";

  const setPreset = (chip: "today" | "month") => {
    setActiveChip(chip);
    setDateRange(chip === "today" ? [today(), today()] : [startOfMonth(), today()]);
  };

  const picker = (
    <Group justify="space-between" wrap="wrap">
      <Chip.Group
        value={activeChip ?? ""}
        onChange={(v) => v && setPreset(v as "today" | "month")}
      >
        <Group gap="xs">
          <Chip value="today" variant="light">Bugün</Chip>
          <Chip value="month" variant="light">Bu Ay</Chip>
        </Group>
      </Chip.Group>
      <DatePickerInput
        type="range"
        placeholder="Tarih aralığı seç"
        leftSection={<IconCalendar size={16} />}
        value={dateRange}
        onChange={(v) => {
          const next = v as [Date | null, Date | null];
          if (!next[0] && !next[1]) {
            // Temizlendi → varsayılan bugüne dön (boş 30-gün fallback'i yerine).
            setPreset("today");
            return;
          }
          setDateRange(next);
          setActiveChip(null); // elle aralık → hızlı seçim vurgusu kalksın
        }}
        clearable
        locale="tr"
        valueFormat="DD.MM.YYYY"
        w={280}
      />
    </Group>
  );

  return (
    <Stack gap="md">
      <PageHeader title="📊 Ürün Satış Raporu" subtitle="En çok satılan ürünler" />
      {picker}

      {isLoading ? <Loader /> :
       rows.length === 0 ? (
        <EmptyState
          icon="📊"
          title={`Bu dönemde satış yok (${rangeLabel})`}
        />
      ) : (
        <>
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">En Çok Satılan İlk 10</Title>
            <BarChart
              h={400}
              data={rows.slice(0, 10).map((r: any) => ({
                name: r.product_name, qty: r.quantity_sold,
              }))}
              dataKey="name"
              series={[{ name: "qty", color: "blue.6", label: "Satılan Adet" }]}
              orientation="vertical"
              yAxisProps={{ width: 150 }}
            />
          </Paper>

          <Table.ScrollContainer minWidth={520}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Ürün</Table.Th>
                  <Table.Th>SKU</Table.Th>
                  <Table.Th ta="right">Adet</Table.Th>
                  <Table.Th ta="right">İkram</Table.Th>
                  <Table.Th ta="right">Fire</Table.Th>
                  <Table.Th ta="right">Ciro</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r: any, idx: number) => (
                  <Table.Tr key={r.product_id}>
                    <Table.Td>{idx + 1}</Table.Td>
                    <Table.Td fw={500}>{r.product_name}</Table.Td>
                    <Table.Td><Badge variant="light">{r.product_sku}</Badge></Table.Td>
                    <Table.Td ta="right">{r.quantity_sold}</Table.Td>
                    <Table.Td ta="right">
                      {r.comp_quantity > 0 ? (
                        <Badge variant="light" color="orange">
                          {r.comp_quantity}
                        </Badge>
                      ) : (
                        <span style={{ color: "var(--mantine-color-dimmed)" }}>—</span>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      {r.waste_quantity > 0 ? (
                        <Badge variant="light" color="red">
                          {r.waste_quantity}
                        </Badge>
                      ) : (
                        <span style={{ color: "var(--mantine-color-dimmed)" }}>—</span>
                      )}
                    </Table.Td>
                    <Table.Td ta="right" fw={700}>
                      <MoneyDisplay minor={r.revenue_minor} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}
    </Stack>
  );
}
